import { ReportItem } from 'store/ducks/Reporting/types';
import * as Styled from './BusinessProcessTree.styled';
import { useTranslation } from 'react-i18next';

export type InfoType =
  | 'process'
  | 'risk'
  | 'control'
  | 'gap'
  | 'audit'
  | 'solution'
  | 'bru'
  | 'evidence'
  | 'processL1'
  | 'processL2'
  | 'processL3'
  | 'processL4';

const infoConfig = (
  t: any
): Record<
  InfoType,
  {
    label: string;
    fields: {
      label: string;
      key: keyof ReportItem;
      transform?: (value: any) => any;
    }[];
  }
> => ({
  process: {
    label: t('info.process.label'),
    fields: [
      { label: t('info.process.type'), key: 'process_type_name' },
      { label: t('info.process.criticality'), key: 'process_criticality_name' }
    ]
  },

  risk: {
    label: t('info.risk.label'),
    fields: [
      { label: t('info.risk.impact'), key: 'risk_instance_impact_consequence' },
      {
        label: t('info.risk.probability'),
        key: 'risk_instance_probability_of_occurrence_name'
      },
      { label: t('info.risk.severity'), key: 'risk_instance_severity_name' }
    ]
  },

  gap: {
    label: t('info.gap.label'),
    fields: [
      { label: t('info.gap.dueDate'), key: 'gap_due_date' },
      { label: t('info.gap.priority'), key: 'priority_name' },
      { label: t('info.gap.owner'), key: 'owner_name' },
      {
        label: t('info.gap.closed'),
        key: 'gap_closed',
        transform: (v) => (v ? t('common.yes') : t('common.no'))
      }
    ]
  },

  audit: {
    label: t('info.audit.label'),
    fields: [
      { label: t('info.audit.dueDate'), key: 'afg_due_date' },
      { label: t('info.audit.closed'), key: 'afg_closed' },
      { label: t('info.audit.ownerNotified'), key: 'afg_owner_notified' },
      { label: t('info.audit.priority'), key: 'afg_priority_name' },
      { label: t('info.audit.source'), key: 'afg_source_name' },
      { label: t('info.audit.owner'), key: 'afg_owner_name' }
    ]
  },

  solution: {
    label: t('info.solution.label'),
    fields: [
      { label: t('info.solution.approved'), key: 'sol_approved' },
      { label: t('info.solution.approver'), key: 'sol_approver_name' },
      {
        label: t('info.solution.approverNotified'),
        key: 'sol_approver_notified'
      },
      { label: t('info.solution.evidenceRequired'), key: 'sol_evd_required' },
      { label: t('info.solution.reviewed'), key: 'sol_reviewed' },
      { label: t('info.solution.reviewer'), key: 'sol_reviewer_name' },
      {
        label: t('info.solution.reviewerNotified'),
        key: 'sol_reviewer_notified'
      },
      { label: t('info.solution.type'), key: 'sol_type_name' }
    ]
  },

  bru: {
    label: t('info.bru.label'),
    fields: [{ label: t('info.bru.name'), key: 'bru_key_value' }]
  },

  control: {
    label: t('info.control.label'),
    fields: [
      { label: t('info.control.frequency'), key: 'control_frequency' },
      {
        label: t('info.control.effectiveness'),
        key: 'control_instance_control_overall_effectiveness_name'
      },
      {
        label: t('info.control.status'),
        key: 'control_instance_control_status_name'
      },
      {
        label: t('info.control.performer'),
        key: 'control_instance_control_performer_name'
      }
    ]
  },

  evidence: {
    label: t('info.evidence.label'),
    fields: [
      { label: t('info.evidence.description'), key: 'evd_description' },
      { label: t('info.evidence.approved'), key: 'evd_approved' },
      { label: t('info.evidence.approver'), key: 'evd_approver_name' },
      { label: t('info.evidence.reviewed'), key: 'evd_reviewed' },
      { label: t('info.evidence.reviewer'), key: 'evd_reviewer_name' },
      { label: t('info.evidence.validFrom'), key: 'evd_valid_from' },
      { label: t('info.evidence.validTo'), key: 'evd_valid_to' },
      { label: t('info.evidence.createdBy'), key: 'evd_created_by' },
      { label: t('info.evidence.createdOn'), key: 'evd_created_on' }
    ]
  },

  processL1: {
    label: t('info.processL1.label'),
    fields: [
      {
        label: t('info.processL1.startDate'),
        key: 'business_process_l1_start_date'
      },
      {
        label: t('info.processL1.endDate'),
        key: 'business_process_l1_end_date'
      }
    ]
  },

  processL2: {
    label: t('info.processL2.label'),
    fields: [
      {
        label: t('info.processL2.startDate'),
        key: 'business_process_l2_start_date'
      },
      {
        label: t('info.processL2.endDate'),
        key: 'business_process_l2_end_date'
      }
    ]
  },

  processL3: {
    label: t('info.processL3.label'),
    fields: [
      {
        label: t('info.processL3.startDate'),
        key: 'business_process_l3_start_date'
      },
      {
        label: t('info.processL3.endDate'),
        key: 'business_process_l3_end_date'
      }
    ]
  },

  processL4: {
    label: t('info.processL4.label'),
    fields: [
      {
        label: t('info.processL4.startDate'),
        key: 'business_process_l4_start_date'
      },
      {
        label: t('info.processL4.endDate'),
        key: 'business_process_l4_end_date'
      }
    ]
  }
});

export const InfoPopover = ({
  type,
  item
}: {
  type: InfoType;
  item: Record<string, any>;
}) => {
  const { t } = useTranslation();

  const config = infoConfig(t)[type];

  return (
    <Styled.PopoverContent>
      {config.fields.map((field) => {
        const value = item[field.key];

        return (
          <p key={field.key}>
            <b>{field.label}:</b>{' '}
            {field.transform
              ? field.transform(value)
              : Array.isArray(value)
              ? value.join(', ')
              : value ?? '-'}
          </p>
        );
      })}
    </Styled.PopoverContent>
  );
};
